/**
 * The sample-editing surface, the live spectrum, and the thumbnail that goes in a list row.
 *
 * Zoom and scroll are expressed as a visible range in seconds rather than a scale factor,
 * so a caller can say "show me the loop region" from a stored edit without ever reasoning
 * about pixels.
 *
 * The reduction cache is the reason a ten-minute file scrolls. Peaks arrive at 400 buckets
 * per second, so ten minutes is 240 000 buckets; walking that array once per frame would
 * spend the whole budget before a single pixel was drawn. The visible window is reduced to
 * two buckets per CSS pixel exactly once per (range, width) pair and reused until one of
 * them changes — a scrub that only moves the playhead redraws from a cached envelope of a
 * few thousand numbers.
 */

import { h, on, onCleanup, schedule } from '../../core/dom';
import { clamp, fadeCurveGain } from '../../model/types';
import type { FadeCurve } from '../../model/types';
import {
  capturePointer,
  createCanvasSurface,
  icon,
  nextId,
  observeSize,
  prefersReducedMotion,
  resolveColour,
  roundedRect,
  themeToken,
  withAlpha,
} from './controls';
import { driveMeterFrames } from './meters';
import type { MeterFrameSubscription } from './meters';

// MARK: - Peak data

/**
 * Mirrors `WaveformPeaks` in `Persistence/PersistenceContracts.swift` field for field, so a
 * peak file computed by either build describes the same picture.
 */
export interface WaveformPeaks {
  /** Negative half of the envelope, one entry per bucket. */
  readonly minima: Float32Array;
  /** Positive half. */
  readonly maxima: Float32Array;
  /** Source seconds represented by one bucket. */
  readonly secondsPerBucket: number;
  readonly duration: number;
}

export const EMPTY_PEAKS: WaveformPeaks = {
  minima: new Float32Array(0),
  maxima: new Float32Array(0),
  secondsPerBucket: 0,
  duration: 0,
};

export function peaksBucketCount(peaks: WaveformPeaks): number {
  return Math.min(peaks.minima.length, peaks.maxima.length);
}

/** Downsamples to `targetCount` buckets for a zoomed-out view without re-reading audio. */
export function resamplePeaks(peaks: WaveformPeaks, targetCount: number): WaveformPeaks {
  const count = peaksBucketCount(peaks);
  if (targetCount <= 0 || count <= targetCount) return peaks;
  const minima = new Float32Array(targetCount);
  const maxima = new Float32Array(targetCount);
  const stride = count / targetCount;
  for (let index = 0; index < targetCount; index++) {
    const start = Math.floor(index * stride);
    const end = Math.max(start + 1, Math.min(count, Math.floor((index + 1) * stride)));
    let lo = 0;
    let hi = 0;
    for (let bucket = start; bucket < end; bucket++) {
      lo = Math.min(lo, peaks.minima[bucket] ?? 0);
      hi = Math.max(hi, peaks.maxima[bucket] ?? 0);
    }
    minima[index] = lo;
    maxima[index] = hi;
  }
  return {
    minima,
    maxima,
    secondsPerBucket: peaks.duration / targetCount,
    duration: peaks.duration,
  };
}

interface EnvelopeWindow {
  /** Bucket-aligned bounds, a little wider than requested, so a zoomed-in envelope does
   *  not slide by up to one bucket as the window moves. */
  readonly start: number;
  readonly end: number;
  readonly minima: Float32Array;
  readonly maxima: Float32Array;
}

const EMPTY_WINDOW: EnvelopeWindow = {
  start: 0,
  end: 0,
  minima: new Float32Array(0),
  maxima: new Float32Array(0),
};

class EnvelopeCache {
  private key = '';
  private cached: EnvelopeWindow = EMPTY_WINDOW;

  window(peaks: WaveformPeaks, lower: number, upper: number, buckets: number): EnvelopeWindow {
    const count = peaksBucketCount(peaks);
    if (count === 0 || peaks.secondsPerBucket <= 0 || buckets <= 0 || upper <= lower) {
      return EMPTY_WINDOW;
    }
    // Identity stand-in: hashing the arrays would cost as much as the reduction it exists
    // to avoid, and (length, resolution, duration) changes whenever the audio does.
    const key = `${count}|${peaks.secondsPerBucket}|${peaks.duration}|${lower}|${upper}|${buckets}`;
    if (key === this.key) return this.cached;

    const first = clamp(Math.floor(lower / peaks.secondsPerBucket), 0, count - 1);
    const last = clamp(Math.ceil(upper / peaks.secondsPerBucket), first + 1, count);
    const source = last - first;
    const outputs = Math.max(1, Math.min(buckets, 8192));
    const minima = new Float32Array(outputs);
    const maxima = new Float32Array(outputs);
    const stride = source / outputs;

    for (let index = 0; index < outputs; index++) {
      const start = first + Math.floor(index * stride);
      const end = Math.max(start + 1, Math.min(last, first + Math.floor((index + 1) * stride)));
      let lo = 0;
      let hi = 0;
      for (let bucket = start; bucket < end; bucket++) {
        lo = Math.min(lo, peaks.minima[bucket] ?? 0);
        hi = Math.max(hi, peaks.maxima[bucket] ?? 0);
      }
      minima[index] = lo;
      maxima[index] = hi;
    }

    this.key = key;
    this.cached = {
      start: first * peaks.secondsPerBucket,
      end: last * peaks.secondsPerBucket,
      minima,
      maxima,
    };
    return this.cached;
  }

  invalidate(): void {
    this.key = '';
    this.cached = EMPTY_WINDOW;
  }
}

// MARK: - Geometry

export interface TimeRange {
  readonly start: number;
  readonly end: number;
}

interface Layout {
  readonly width: number;
  readonly height: number;
  readonly lower: number;
  readonly span: number;
  readonly midY: number;
  /** Half-height available to the envelope, leaving room for the stroke. */
  readonly amplitude: number;
}

function makeLayout(width: number, height: number, lower: number, span: number): Layout {
  return {
    width,
    height,
    lower,
    span,
    midY: height / 2,
    amplitude: Math.max(1, height / 2 - 3),
  };
}

function timeToX(layout: Layout, time: number): number {
  return ((time - layout.lower) / layout.span) * layout.width;
}

function xToTime(layout: Layout, x: number): number {
  return layout.lower + (x / Math.max(1, layout.width)) * layout.span;
}

export function formatWaveformTime(seconds: number): string {
  const total = Math.max(0, seconds);
  const minutes = Math.floor(total / 60);
  const rest = total - minutes * 60;
  if (minutes === 0) return `${rest.toFixed(2)}s`;
  return `${minutes}:${rest < 10 ? '0' : ''}${rest.toFixed(1)}`;
}

// MARK: - WaveformView

export type WaveformInteraction = 'scrub' | 'select';

export interface WaveformViewOptions {
  readonly peaks?: WaveformPeaks;
  readonly sampleRate?: number;
  /** What a one-finger drag on empty waveform does. Handles drag in both modes. */
  readonly interaction?: WaveformInteraction;
  readonly allowsZoom?: boolean;
  readonly showsTimeRuler?: boolean;
  readonly tint?: string;
  readonly fadeCurve?: FadeCurve;
  readonly label?: string;
  readonly onScrub?: (time: number) => void;
  /** The finger came off. Lets a caller stop whatever the scrub was playing. */
  readonly onScrubEnded?: () => void;
  readonly onSelectionChanged?: (range: TimeRange | null) => void;
  readonly onVisibleRangeChanged?: (range: TimeRange) => void;
}

export interface WaveformViewHandle {
  readonly el: HTMLElement;
  setPeaks(peaks: WaveformPeaks): void;
  setPlayhead(time: number | null): void;
  setSelection(range: TimeRange | null): void;
  setLoopRange(range: TimeRange | null): void;
  setSliceMarkers(markers: readonly number[]): void;
  setFades(fadeIn: number, fadeOut: number, curve?: FadeCurve): void;
  setVisibleRange(range: TimeRange): void;
  zoomToFit(): void;
  zoomBy(factor: number): void;
  readonly visibleRange: TimeRange;
  readonly selection: TimeRange | null;
  destroy(): void;
}

/** Below roughly a millisecond there is nothing left to see: peaks are bucketed anyway. */
const MINIMUM_VISIBLE_SAMPLES = 64;
/** Half a touch target either side of an edge, so a 12 px grip owns a full 44 px. */
const HANDLE_GRAB = 22;
const SNAP_PIXELS = 6;

type DragMode = 'idle' | 'scrub' | 'selectionStart' | 'selectionEnd' | 'creatingSelection';

export function waveformView(options: WaveformViewOptions = {}): WaveformViewHandle {
  const sampleRate = options.sampleRate ?? 48000;
  const allowsZoom = options.allowsZoom !== false;
  const interaction: WaveformInteraction = options.interaction ?? 'scrub';

  let peaks = options.peaks ?? EMPTY_PEAKS;
  let selection: TimeRange | null = null;
  let playhead: number | null = null;
  let loopRange: TimeRange | null = null;
  let sliceMarkers: readonly number[] = [];
  let fadeIn = 0;
  let fadeOut = 0;
  let fadeCurve: FadeCurve = options.fadeCurve ?? 'equalPower';
  let visibleLower = 0;
  let visibleSpan = 0;

  const cache = new EnvelopeCache();
  const surface = createCanvasSurface('pl-wave__canvas', () => requestDraw());

  const rulerStart = h('span', { class: 'pl-wave__time' });
  const rulerSelection = h('span', { class: 'pl-wave__time pl-wave__time--sel' });
  const rulerEnd = h('span', { class: 'pl-wave__time' });
  const ruler = h(
    'div',
    { class: 'pl-wave__ruler numeric', 'aria-hidden': 'true' },
    rulerStart,
    rulerSelection,
    rulerEnd,
  );

  const hintId = `pl-wave-hint-${nextId()}`;
  const hint = h(
    'span',
    { class: 'visually-hidden', id: hintId },
    'Left and right arrows move the playhead. Plus and minus zoom. Press 0 to fit the whole sample.',
  );

  const el = h(
    'div',
    {
      class: 'pl-wave instrument',
      role: 'slider',
      tabindex: '0',
      'aria-label': options.label ?? 'Waveform',
      'aria-valuemin': '0',
      'aria-valuemax': '0',
      'aria-valuenow': '0',
      'aria-describedby': hintId,
      style: { '--wave-tint': options.tint ?? 'var(--accent-magenta)' },
    },
    surface.canvas,
    options.showsTimeRuler === false ? null : ruler,
    hint,
  );

  // --- Derived geometry ------------------------------------------------------------

  const duration = (): number => Math.max(peaks.duration, 0.0001);
  const minimumSpan = (): number => Math.max(MINIMUM_VISIBLE_SAMPLES / Math.max(sampleRate, 1), 0.0001);

  /** An empty or out-of-bounds window means "show the whole file". */
  function range(): TimeRange {
    const lower = Math.max(0, visibleLower);
    const upper = lower + visibleSpan;
    if (visibleSpan < minimumSpan() * 0.5 || upper > duration() + 0.001) {
      return { start: 0, end: duration() };
    }
    return { start: lower, end: upper };
  }

  function currentLayout(): Layout {
    const window = range();
    return makeLayout(
      surface.width,
      surface.height,
      window.start,
      Math.max(window.end - window.start, minimumSpan()),
    );
  }

  function setVisible(lower: number, rawSpan: number, notify = true): void {
    const span = clamp(rawSpan, minimumSpan(), duration());
    const start = clamp(lower, 0, Math.max(0, duration() - span));
    if (start === visibleLower && span === visibleSpan) return;
    visibleLower = Number.isFinite(start) ? start : 0;
    visibleSpan = span;
    requestDraw();
    if (notify) options.onVisibleRangeChanged?.({ start: visibleLower, end: visibleLower + span });
  }

  function zoom(newSpan: number, anchorTime: number, anchorUnit: number): void {
    const span = clamp(newSpan, minimumSpan(), duration());
    setVisible(anchorTime - span * clamp(anchorUnit, 0, 1), span);
  }

  // --- Drawing ---------------------------------------------------------------------

  let drawScheduled: (() => void) | null = null;

  function requestDraw(): void {
    if (drawScheduled !== null) return;
    drawScheduled = schedule(() => {
      drawScheduled = null;
      draw();
    });
  }

  function draw(): void {
    const context = surface.context;
    if (typeof context.clearRect !== 'function') return;
    const layout = currentLayout();
    if (layout.width <= 2 || layout.height <= 2) return;
    context.clearRect(0, 0, layout.width, layout.height);

    const tint = resolveColour(options.tint, '#ec5cff');

    context.strokeStyle = themeToken('--hairline-strong', '#33333e');
    context.lineWidth = 1;
    context.beginPath();
    context.moveTo(0, layout.midY);
    context.lineTo(layout.width, layout.midY);
    context.stroke();

    drawEnvelope(context, layout, tint);
    drawFades(context, layout);
    drawSelectionScrim(context, layout);
    drawLoop(context, layout);
    drawSlices(context, layout);
    drawSelectionHandles(context, layout);
    drawPlayhead(context, layout);

    updateRuler();
    updateAria();
  }

  function drawEnvelope(context: CanvasRenderingContext2D, layout: Layout, tint: string): void {
    // Two buckets per CSS pixel: enough that the envelope keeps its texture on a Retina
    // screen, few enough that the path stays a few thousand points.
    const buckets = Math.max(1, Math.min(Math.round(layout.width * 2), 4096));
    const window = cache.window(peaks, layout.lower, layout.lower + layout.span, buckets);
    const count = window.minima.length;
    if (count === 0 || window.end <= window.start) return;

    const step = (window.end - window.start) / count;
    const floorAmplitude = 0.75;

    context.beginPath();
    for (let index = 0; index < count; index++) {
      const time = window.start + step * (index + 0.5);
      const value = clamp(window.maxima[index] ?? 0, 0, 1);
      const y = layout.midY - Math.max(floorAmplitude, value * layout.amplitude);
      const x = timeToX(layout, time);
      if (index === 0) context.moveTo(x, y);
      else context.lineTo(x, y);
    }
    for (let index = count - 1; index >= 0; index--) {
      const time = window.start + step * (index + 0.5);
      const value = clamp(-(window.minima[index] ?? 0), 0, 1);
      context.lineTo(
        timeToX(layout, time),
        layout.midY + Math.max(floorAmplitude, value * layout.amplitude),
      );
    }
    context.closePath();

    const gradient = context.createLinearGradient(0, 0, 0, layout.height);
    gradient.addColorStop(0, withAlpha(tint, 0.95));
    gradient.addColorStop(1, withAlpha(tint, 0.5));
    context.fillStyle = gradient;
    context.fill();
    context.strokeStyle = tint;
    context.lineWidth = 0.75;
    context.stroke();
  }

  /** Fades are measured against the selection when there is one, matching `SampleEdit`. */
  function fadeRegion(): TimeRange {
    return selection ?? { start: 0, end: duration() };
  }

  function drawFades(context: CanvasRenderingContext2D, layout: Layout): void {
    const region = fadeRegion();
    if (fadeIn > 0) {
      const end = Math.min(region.start + fadeIn, region.end);
      if (end > region.start) {
        drawFadeCurve(context, layout, region.start, end, (fraction) => fadeCurveGain(fadeCurve, fraction));
      }
    }
    if (fadeOut > 0) {
      const start = Math.max(region.end - fadeOut, region.start);
      if (region.end > start) {
        drawFadeCurve(context, layout, start, region.end, (fraction) => fadeCurveGain(fadeCurve, 1 - fraction));
      }
    }
  }

  function drawFadeCurve(
    context: CanvasRenderingContext2D,
    layout: Layout,
    start: number,
    end: number,
    gain: (fraction: number) => number,
  ): void {
    const steps = 32;
    const scrim = withAlpha(themeToken('--ink-base', '#08080a'), 0.62);
    const points: Array<{ x: number; offset: number }> = [];
    for (let index = 0; index <= steps; index++) {
      const fraction = index / steps;
      const time = start + (end - start) * fraction;
      points.push({
        x: timeToX(layout, time),
        offset: clamp(gain(fraction), 0, 1) * layout.amplitude,
      });
    }
    const first = points[0];
    const last = points[points.length - 1];
    if (first === undefined || last === undefined) return;

    // Shading what the fade removes reads faster than drawing only the curve: the user sees
    // the audio that is left, not a line they have to interpret.
    context.fillStyle = scrim;
    context.beginPath();
    context.moveTo(first.x, 0);
    for (const point of points) context.lineTo(point.x, layout.midY - point.offset);
    context.lineTo(last.x, 0);
    context.closePath();
    context.fill();

    context.beginPath();
    context.moveTo(first.x, layout.height);
    for (const point of points) context.lineTo(point.x, layout.midY + point.offset);
    context.lineTo(last.x, layout.height);
    context.closePath();
    context.fill();

    context.strokeStyle = withAlpha(themeToken('--tone-primary', '#ffffff'), 0.7);
    context.lineWidth = 1.5;
    context.beginPath();
    points.forEach((point, index) => {
      if (index === 0) context.moveTo(point.x, layout.midY - point.offset);
      else context.lineTo(point.x, layout.midY - point.offset);
    });
    context.stroke();
  }

  function drawSelectionScrim(context: CanvasRenderingContext2D, layout: Layout): void {
    if (selection === null) return;
    const startX = timeToX(layout, selection.start);
    const endX = timeToX(layout, selection.end);
    context.fillStyle = withAlpha(themeToken('--ink-base', '#08080a'), 0.6);
    if (startX > 0) context.fillRect(0, 0, Math.min(startX, layout.width), layout.height);
    if (endX < layout.width) {
      context.fillRect(Math.max(0, endX), 0, layout.width - Math.max(0, endX), layout.height);
    }
  }

  function drawLoop(context: CanvasRenderingContext2D, layout: Layout): void {
    if (loopRange === null || loopRange.end <= loopRange.start) return;
    const amber = themeToken('--accent-amber', '#f5c518');
    const startX = timeToX(layout, loopRange.start);
    const endX = timeToX(layout, loopRange.end);
    const foot = 8;

    context.fillStyle = withAlpha(amber, 0.07);
    context.fillRect(startX, 0, Math.max(0, endX - startX), layout.height);

    // Inward feet turn two lines into a bracket, which distinguishes the loop from the
    // selection without relying on colour.
    context.strokeStyle = amber;
    context.lineWidth = 1.5;
    context.beginPath();
    context.moveTo(startX, 0);
    context.lineTo(startX, layout.height);
    context.moveTo(endX, 0);
    context.lineTo(endX, layout.height);
    context.moveTo(startX, 1);
    context.lineTo(startX + foot, 1);
    context.moveTo(startX, layout.height - 1);
    context.lineTo(startX + foot, layout.height - 1);
    context.moveTo(endX, 1);
    context.lineTo(endX - foot, 1);
    context.moveTo(endX, layout.height - 1);
    context.lineTo(endX - foot, layout.height - 1);
    context.stroke();
  }

  function drawSlices(context: CanvasRenderingContext2D, layout: Layout): void {
    if (sliceMarkers.length === 0) return;
    const violet = themeToken('--accent-violet', '#7b3ff2');
    const window = range();
    context.save();
    context.setLineDash([2, 3]);
    context.strokeStyle = withAlpha(violet, 0.8);
    context.lineWidth = 1;
    context.beginPath();
    for (const marker of sliceMarkers) {
      if (marker < window.start || marker > window.end) continue;
      const x = timeToX(layout, marker);
      context.moveTo(x, 0);
      context.lineTo(x, layout.height);
    }
    context.stroke();
    context.restore();

    context.fillStyle = violet;
    context.beginPath();
    for (const marker of sliceMarkers) {
      if (marker < window.start || marker > window.end) continue;
      const x = timeToX(layout, marker);
      context.moveTo(x - 4, layout.height);
      context.lineTo(x + 4, layout.height);
      context.lineTo(x, layout.height - 6);
      context.closePath();
    }
    context.fill();
  }

  function drawSelectionHandles(context: CanvasRenderingContext2D, layout: Layout): void {
    if (selection === null) return;
    drawHandle(context, layout, selection.start);
    drawHandle(context, layout, selection.end);
  }

  function drawHandle(context: CanvasRenderingContext2D, layout: Layout, time: number): void {
    const x = timeToX(layout, time);
    if (x < -12 || x > layout.width + 12) return;
    const acid = themeToken('--accent-acid', '#dcff33');

    context.strokeStyle = acid;
    context.lineWidth = 2;
    context.beginPath();
    context.moveTo(x, 0);
    context.lineTo(x, layout.height);
    context.stroke();

    const gripHeight = Math.min(30, Math.max(12, layout.height - 8));
    context.fillStyle = acid;
    roundedRect(context, x - 6, layout.midY - gripHeight / 2, 12, gripHeight, 4);
    context.fill();

    context.strokeStyle = withAlpha(themeToken('--ink-base', '#08080a'), 0.75);
    context.lineWidth = 1;
    context.beginPath();
    context.moveTo(x - 2, layout.midY - gripHeight / 2 + 7);
    context.lineTo(x - 2, layout.midY + gripHeight / 2 - 7);
    context.moveTo(x + 2, layout.midY - gripHeight / 2 + 7);
    context.lineTo(x + 2, layout.midY + gripHeight / 2 - 7);
    context.stroke();
  }

  function drawPlayhead(context: CanvasRenderingContext2D, layout: Layout): void {
    const window = range();
    if (playhead === null || playhead < window.start || playhead > window.end) return;
    const x = timeToX(layout, playhead);
    const white = themeToken('--tone-primary', '#ffffff');

    context.strokeStyle = white;
    context.lineWidth = 1.5;
    context.beginPath();
    context.moveTo(x, 0);
    context.lineTo(x, layout.height);
    context.stroke();

    context.fillStyle = white;
    context.beginPath();
    context.moveTo(x - 5, 0);
    context.lineTo(x + 5, 0);
    context.lineTo(x, 7);
    context.closePath();
    context.fill();
  }

  function updateRuler(): void {
    if (options.showsTimeRuler === false) return;
    const window = range();
    rulerStart.textContent = formatWaveformTime(window.start);
    rulerEnd.textContent = formatWaveformTime(window.end);
    if (selection === null) {
      rulerSelection.textContent = '';
      rulerSelection.hidden = true;
    } else {
      rulerSelection.textContent = `SEL ${formatWaveformTime(selection.end - selection.start)}`;
      rulerSelection.hidden = false;
    }
  }

  function updateAria(): void {
    const window = range();
    el.setAttribute('aria-valuemin', '0');
    el.setAttribute('aria-valuemax', duration().toFixed(3));
    el.setAttribute('aria-valuenow', (playhead ?? window.start).toFixed(3));
    const parts = [`${formatWaveformTime(duration())} long`];
    if (selection !== null) {
      parts.push(
        `selection ${formatWaveformTime(selection.start)} to ${formatWaveformTime(selection.end)}`,
      );
    }
    if (playhead !== null) parts.push(`playhead ${formatWaveformTime(playhead)}`);
    if (window.start > 0 || window.end < duration()) {
      parts.push(`showing ${formatWaveformTime(window.start)} to ${formatWaveformTime(window.end)}`);
    }
    el.setAttribute('aria-valuetext', parts.join(', '));
  }

  // --- Pointer interaction ---------------------------------------------------------

  interface LivePointer {
    x: number;
  }

  const pointers = new Map<number, LivePointer>();
  let mode: DragMode = 'idle';
  let selectionAnchor = 0;
  let zooming = false;
  let zoomStartSpan = 1;
  let zoomStartDistance = 1;
  let zoomAnchorTime = 0;
  let zoomAnchorUnit = 0.5;

  const localX = (clientX: number): number =>
    clientX - surface.canvas.getBoundingClientRect().left;

  function boundedTime(x: number, layout: Layout): number {
    return clamp(xToTime(layout, clamp(x, 0, layout.width)), 0, duration());
  }

  /** Dragging past an edge scrolls the window — the only way to reach the rest of a
   *  zoomed-in file with one finger. */
  function scrubTime(x: number, layout: Layout): number {
    if (x < 0 || x > layout.width) {
      const overflow = (x < 0 ? x : x - layout.width) / Math.max(layout.width, 1);
      setVisible(layout.lower + overflow * layout.span * 0.25, layout.span);
      return boundedTime(x, currentLayout());
    }
    return boundedTime(x, layout);
  }

  /** Pulls an edge onto the file bounds or a slice marker within six pixels, so trimming to
   *  a transient does not require sub-pixel aim. */
  function snapped(time: number, layout: Layout): number {
    const tolerance = (SNAP_PIXELS / Math.max(layout.width, 1)) * layout.span;
    let best = time;
    let bestDistance = tolerance;
    for (const candidate of [0, duration(), ...sliceMarkers]) {
      const distance = Math.abs(candidate - time);
      if (distance < bestDistance) {
        best = candidate;
        bestDistance = distance;
      }
    }
    return best;
  }

  function beginMode(x: number, layout: Layout): DragMode {
    if (selection !== null) {
      const toStart = Math.abs(x - timeToX(layout, selection.start));
      const toEnd = Math.abs(x - timeToX(layout, selection.end));
      if (toStart <= HANDLE_GRAB || toEnd <= HANDLE_GRAB) {
        return toStart <= toEnd ? 'selectionStart' : 'selectionEnd';
      }
    }
    return interaction === 'select' ? 'creatingSelection' : 'scrub';
  }

  /**
   * Rate-limits scrub reports to roughly one per audio grain.
   *
   * A `pointermove` stream arrives at the display's refresh rate, and starting a preview
   * voice on every one of them would be a hundred and twenty overlapping sources a second.
   */
  const SCRUB_INTERVAL_MS = 70;
  let lastScrubAt = 0;

  function emitScrub(time: number, force = false): void {
    if (options.onScrub === undefined) return;
    const at = performance.now();
    if (!force && at - lastScrubAt < SCRUB_INTERVAL_MS) return;
    lastScrubAt = at;
    options.onScrub(time);
  }

  function updateSelection(x: number, layout: Layout): void {
    const time = snapped(scrubTime(x, layout), layout);
    if (mode === 'selectionStart' && selection !== null) {
      selection = { start: Math.min(time, selection.end), end: selection.end };
    } else if (mode === 'selectionEnd' && selection !== null) {
      selection = { start: selection.start, end: Math.max(time, selection.start) };
    } else if (mode === 'creatingSelection') {
      selection = { start: Math.min(time, selectionAnchor), end: Math.max(time, selectionAnchor) };
    }
    requestDraw();
  }

  onCleanup(
    el,
    on(el, 'pointerdown', ((event: PointerEvent) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      event.preventDefault();
      capturePointer(el, event.pointerId);
      pointers.set(event.pointerId, { x: event.clientX });

      if (pointers.size === 2 && allowsZoom) {
        const [a, b] = [...pointers.values()];
        if (a === undefined || b === undefined) return;
        zooming = true;
        mode = 'idle';
        const layout = currentLayout();
        zoomStartSpan = layout.span;
        zoomStartDistance = Math.max(1, Math.abs(a.x - b.x));
        zoomAnchorUnit = clamp(localX((a.x + b.x) / 2) / Math.max(1, layout.width), 0, 1);
        zoomAnchorTime = layout.lower + layout.span * zoomAnchorUnit;
        return;
      }
      if (pointers.size !== 1) return;

      const layout = currentLayout();
      const x = localX(event.clientX);
      mode = beginMode(x, layout);
      if (mode === 'creatingSelection') {
        selectionAnchor = boundedTime(x, layout);
        selection = { start: selectionAnchor, end: selectionAnchor };
      }
      // Scrubbing is not a separate mode a touch can reach: the editor passes
      // `interaction: 'select'`, so `beginMode` never returns `'scrub'` there and `onScrub`
      // only ever fired from the arrow keys — tapping the waveform on a phone started no
      // audio at all. A finger on the waveform now always says where it is, and dragging it
      // both moves the selection edge and plays what is under it, which is what scrubbing
      // is. The tail of a scrub is announced with `null` so the caller can stop the preview.
      emitScrub(scrubTime(x, layout));
      if (mode !== 'scrub') updateSelection(x, layout);
      el.classList.add('is-dragging');
    }) as EventListener, { passive: false }),
  );

  onCleanup(
    el,
    on(el, 'pointermove', ((event: PointerEvent) => {
      const live = pointers.get(event.pointerId);
      if (live === undefined) return;
      live.x = event.clientX;

      if (zooming) {
        const [a, b] = [...pointers.values()];
        if (a === undefined || b === undefined) return;
        const distance = Math.max(1, Math.abs(a.x - b.x));
        zoom((zoomStartSpan * zoomStartDistance) / distance, zoomAnchorTime, zoomAnchorUnit);
        return;
      }

      const layout = currentLayout();
      const x = localX(event.clientX);
      if (mode === 'idle') return;
      emitScrub(scrubTime(x, layout));
      if (mode !== 'scrub') updateSelection(x, layout);
    }) as EventListener),
  );

  const endPointer = ((event: PointerEvent) => {
    if (!pointers.delete(event.pointerId)) return;
    if (pointers.size < 2) zooming = false;
    if (pointers.size > 0) return;
    if (mode !== 'idle') options.onScrubEnded?.();
    if (mode !== 'idle' && mode !== 'scrub') options.onSelectionChanged?.(selection);
    mode = 'idle';
    el.classList.remove('is-dragging');
  }) as EventListener;

  onCleanup(el, on(el, 'pointerup', endPointer));
  onCleanup(el, on(el, 'pointercancel', endPointer));

  // Trackpad and mouse-wheel zoom, for the iPad-with-keyboard and desktop cases. Ctrl+wheel
  // is the pinch gesture every browser synthesises, so honouring it costs nothing.
  onCleanup(
    el,
    on(el, 'wheel', ((event: WheelEvent) => {
      if (!allowsZoom) return;
      event.preventDefault();
      const layout = currentLayout();
      if (event.ctrlKey || event.metaKey) {
        const unit = clamp(localX(event.clientX) / Math.max(1, layout.width), 0, 1);
        zoom(layout.span * Math.exp(event.deltaY * 0.01), layout.lower + layout.span * unit, unit);
      } else {
        setVisible(layout.lower + (event.deltaX / Math.max(1, layout.width)) * layout.span, layout.span);
      }
    }) as EventListener, { passive: false }),
  );

  // --- Keyboard --------------------------------------------------------------------

  onCleanup(
    el,
    on(el, 'keydown', ((event: KeyboardEvent) => {
      const layout = currentLayout();
      const step = layout.span / (event.shiftKey ? 4 : 20);
      const current = playhead ?? layout.lower;
      switch (event.key) {
        case 'ArrowRight':
          event.preventDefault();
          options.onScrub?.(clamp(current + step, 0, duration()));
          return;
        case 'ArrowLeft':
          event.preventDefault();
          options.onScrub?.(clamp(current - step, 0, duration()));
          return;
        case 'Home':
          event.preventDefault();
          options.onScrub?.(0);
          return;
        case 'End':
          event.preventDefault();
          options.onScrub?.(duration());
          return;
        case '+':
        case '=':
          event.preventDefault();
          zoomBy(0.5);
          return;
        case '-':
        case '_':
          event.preventDefault();
          zoomBy(2);
          return;
        case '0':
          event.preventDefault();
          zoomToFit();
          return;
        default:
      }
    }) as EventListener),
  );

  function zoomBy(factor: number): void {
    const layout = currentLayout();
    zoom(layout.span * factor, layout.lower + layout.span / 2, 0.5);
  }

  function zoomToFit(): void {
    setVisible(0, duration());
  }

  onCleanup(el, observeSize(surface.canvas, () => requestDraw()));
  onCleanup(el, () => {
    drawScheduled?.();
    surface.dispose();
  });

  surface.measure();
  requestDraw();

  return {
    el,
    setPeaks(next: WaveformPeaks): void {
      peaks = next;
      cache.invalidate();
      if (visibleSpan > duration()) setVisible(0, duration(), false);
      requestDraw();
    },
    setPlayhead(time: number | null): void {
      playhead = time === null ? null : clamp(time, 0, duration());
      requestDraw();
    },
    setSelection(next: TimeRange | null): void {
      selection = next === null ? null : { start: Math.min(next.start, next.end), end: Math.max(next.start, next.end) };
      requestDraw();
    },
    setLoopRange(next: TimeRange | null): void {
      loopRange = next;
      requestDraw();
    },
    setSliceMarkers(markers: readonly number[]): void {
      sliceMarkers = markers;
      requestDraw();
    },
    setFades(nextIn: number, nextOut: number, curve?: FadeCurve): void {
      fadeIn = Math.max(0, nextIn);
      fadeOut = Math.max(0, nextOut);
      if (curve !== undefined) fadeCurve = curve;
      requestDraw();
    },
    setVisibleRange(next: TimeRange): void {
      setVisible(next.start, next.end - next.start, false);
    },
    zoomToFit,
    zoomBy,
    get visibleRange() {
      return range();
    },
    get selection() {
      return selection;
    },
    destroy(): void {
      drawScheduled?.();
      surface.dispose();
      el.remove();
    },
  };
}

// MARK: - SpectrumView

export interface SpectrumViewOptions {
  readonly bands?: number;
  readonly tint?: string;
  readonly label?: string;
  readonly class?: string;
}

export interface SpectrumViewHandle {
  readonly el: HTMLElement;
  /** Normalised band magnitudes, 0…1, low band first. */
  setLevels(levels: ArrayLike<number>): void;
  destroy(): void;
}

/** Caps fall slowly enough to read a transient that has already gone. */
const CAP_FALL_PER_SECOND = 0.55;
const SPECTRUM_ATTACK = 0.55;
const SPECTRUM_RELEASE = 0.12;

export function spectrumView(options: SpectrumViewOptions = {}): SpectrumViewHandle {
  const bandCount = Math.max(4, options.bands ?? 24);
  const smoothed = new Float32Array(bandCount);
  const caps = new Float32Array(bandCount);
  let lastFrameAt = 0;
  let dirty = true;

  // Declared before the surface: the resize callback can fire before the subscription is
  // assigned, and a temporal-dead-zone throw inside a ResizeObserver would be silent.
  let subscription: MeterFrameSubscription | null = null;

  const surface = createCanvasSurface('pl-spectrum__canvas', () => {
    dirty = true;
    subscription?.wake();
  });

  const el = h(
    'div',
    { class: ['pl-spectrum', options.class ?? ''], role: 'img', 'aria-label': options.label ?? 'Frequency spectrum' },
    surface.canvas,
  );

  function draw(): void {
    const context = surface.context;
    if (typeof context.clearRect !== 'function') return;
    const width = surface.width;
    const height = surface.height;
    if (width <= 2 || height <= 2) return;
    context.clearRect(0, 0, width, height);

    const tint = resolveColour(options.tint, '#ec5cff');
    const gap = 2;
    const barWidth = Math.max(1, (width - gap * (bandCount - 1)) / bandCount);
    const radius = Math.min(2, barWidth / 2);

    for (let index = 0; index < bandCount; index++) {
      const x = index * (barWidth + gap);
      const value = clamp(smoothed[index] ?? 0, 0, 1);
      context.fillStyle = withAlpha(tint, 0.12);
      roundedRect(context, x, 0, barWidth, height, radius);
      context.fill();

      if (value > 0.001) {
        const barHeight = Math.max(1, value * height);
        context.fillStyle = tint;
        roundedRect(context, x, height - barHeight, barWidth, barHeight, radius);
        context.fill();
      }

      const cap = clamp(caps[index] ?? 0, 0, 1);
      if (cap > 0.004) {
        context.fillStyle = themeToken('--tone-primary', '#ffffff');
        context.fillRect(x, Math.max(0, height - cap * height - 2), barWidth, 2);
      }
    }
  }

  subscription = driveMeterFrames((now: number) => {
    const elapsed = lastFrameAt === 0 ? 0 : Math.min(0.25, (now - lastFrameAt) / 1000);
    lastFrameAt = now;
    let live = false;

    if (!prefersReducedMotion()) {
      const fall = CAP_FALL_PER_SECOND * elapsed;
      for (let index = 0; index < bandCount; index++) {
        const cap = caps[index] ?? 0;
        if (cap <= 0.004) continue;
        caps[index] = Math.max(0, cap - fall);
        live = true;
        dirty = true;
      }
    }

    if (dirty) {
      dirty = false;
      draw();
    }
    return live;
  });

  onCleanup(el, () => {
    subscription?.stop();
    surface.dispose();
  });

  surface.measure();
  subscription.wake();

  return {
    el,
    setLevels(levels: ArrayLike<number>): void {
      const count = Math.min(bandCount, levels.length);
      for (let index = 0; index < count; index++) {
        const raw = clamp(levels[index] ?? 0, 0, 1);
        const previous = smoothed[index] ?? 0;
        // Fast attack so transients are not flattened, slow release so percussive material
        // does not strobe.
        const coefficient = raw > previous ? SPECTRUM_ATTACK : SPECTRUM_RELEASE;
        const next = previous + (raw - previous) * coefficient;
        smoothed[index] = next;
        if (next > (caps[index] ?? 0)) caps[index] = next;
      }
      for (let index = count; index < bandCount; index++) smoothed[index] = 0;
      dirty = true;
      subscription?.wake();
    },
    destroy(): void {
      subscription?.stop();
      surface.dispose();
      el.remove();
    },
  };
}

// MARK: - MiniWaveform

export interface MiniWaveformOptions {
  readonly peaks?: WaveformPeaks;
  readonly tint?: string;
  readonly class?: string;
  /** Shown instead of an envelope when the row has no peak data yet. */
  readonly placeholderSymbol?: string;
}

export interface MiniWaveformHandle {
  readonly el: HTMLElement;
  setPeaks(peaks: WaveformPeaks): void;
  destroy(): void;
}

/**
 * The static thumbnail for library rows and pack listings. Hidden from VoiceOver on
 * purpose: it repeats what the row's own label already says, and a shape is not readable.
 */
export interface ScopeViewOptions {
  /** How many columns the trace is drawn from. */
  readonly points?: number;
  readonly tint?: string;
  readonly class?: string;
  readonly label?: string;
}

export interface ScopeViewHandle {
  readonly el: HTMLElement;
  setTrace(values: Float32Array): void;
  destroy(): void;
}

/**
 * A live oscilloscope over the master bus, and the amplitude-and-frequency reactive glow the
 * brief asks for.
 *
 * The glow is not decoration bolted on: its strength is the trace's own peak and its position
 * is the trace's zero-crossing density, which is a cheap stand-in for brightness. Nothing
 * lights unless something is sounding, which is the same rule the pads follow — colour marks
 * what is playing, never what merely exists.
 */
export function scopeView(options: ScopeViewOptions = {}): ScopeViewHandle {
  const pointCount = Math.max(16, options.points ?? 96);
  const trace = new Float32Array(pointCount);
  let peak = 0;
  let brightness = 0.5;
  let dirty = true;
  let subscription: MeterFrameSubscription | null = null;

  const surface = createCanvasSurface('pl-scope__canvas', () => {
    dirty = true;
    subscription?.wake();
  });

  const glow = h('span', { class: 'pl-scope__glow', 'aria-hidden': 'true' });
  const el = h(
    'div',
    { class: ['pl-scope', options.class ?? ''], role: 'img', 'aria-label': options.label ?? 'Live waveform' },
    glow,
    surface.canvas,
  );

  function draw(): void {
    const context = surface.context;
    if (typeof context.clearRect !== 'function') return;
    const width = surface.width;
    const height = surface.height;
    if (width <= 2 || height <= 2) return;
    context.clearRect(0, 0, width, height);

    const tint = resolveColour(options.tint, '#ec5cff');
    const middle = height / 2;
    context.strokeStyle = withAlpha(tint, 0.25 + peak * 0.75);
    context.lineWidth = 1.5;
    context.lineJoin = 'round';
    context.beginPath();
    for (let index = 0; index < pointCount; index++) {
      const x = (index / (pointCount - 1)) * width;
      const y = middle - clamp(trace[index] ?? 0, -1, 1) * (middle - 2);
      if (index === 0) context.moveTo(x, y);
      else context.lineTo(x, y);
    }
    context.stroke();

    // One element, one hue, driven by what is sounding. No cycling, no second accent.
    glow.style.opacity = String(clamp(peak * 1.4, 0, 1));
    glow.style.transform = `translateY(${((0.5 - brightness) * 40).toFixed(1)}%) scale(${(0.8 + peak * 0.5).toFixed(2)})`;
  }

  function measure(values: Float32Array): void {
    let highest = 0;
    let crossings = 0;
    let previous = values[0] ?? 0;
    for (let index = 0; index < values.length; index++) {
      const value = values[index] ?? 0;
      const magnitude = Math.abs(value);
      if (magnitude > highest) highest = magnitude;
      if ((previous < 0) !== (value < 0)) crossings += 1;
      previous = value;
    }
    // Decay rather than snap, so a transient reads as a flare instead of a flicker.
    peak = Math.max(highest, peak * 0.86);
    const density = values.length <= 1 ? 0 : crossings / (values.length - 1);
    brightness = brightness * 0.85 + clamp(density * 3, 0, 1) * 0.15;
  }

  subscription = driveMeterFrames(() => {
    if (!dirty) return peak > 0.004;
    dirty = false;
    draw();
    // Keep the frame loop alive while the glow still has somewhere to fall to.
    return peak > 0.004;
  });

  return {
    el,
    setTrace(values: Float32Array): void {
      const step = values.length / pointCount;
      for (let index = 0; index < pointCount; index++) trace[index] = values[Math.floor(index * step)] ?? 0;
      measure(trace);
      dirty = true;
      subscription?.wake();
    },
    destroy(): void {
      subscription?.stop();
      subscription = null;
      surface.dispose();
      el.remove();
    },
  };
}

export function miniWaveform(options: MiniWaveformOptions = {}): MiniWaveformHandle {
  let peaks = options.peaks ?? EMPTY_PEAKS;
  const cache = new EnvelopeCache();
  const placeholder = h(
    'span',
    { class: 'pl-mini__placeholder' },
    icon(options.placeholderSymbol ?? 'waveform', { size: 16 }),
  );

  const surface = createCanvasSurface('pl-mini__canvas', () => draw());
  const el = h(
    'div',
    { class: ['pl-mini', options.class ?? ''], 'aria-hidden': 'true' },
    surface.canvas,
    placeholder,
  );

  function draw(): void {
    const context = surface.context;
    if (typeof context.clearRect !== 'function') return;
    const width = surface.width;
    const height = surface.height;
    if (width <= 1 || height <= 1) return;
    context.clearRect(0, 0, width, height);

    const count = peaksBucketCount(peaks);
    placeholder.hidden = count > 0;
    if (count === 0) return;

    const buckets = Math.max(1, Math.min(Math.round(width), 512));
    const window = cache.window(peaks, 0, peaks.duration, buckets);
    const outputs = window.minima.length;
    if (outputs === 0) return;

    const midY = height / 2;
    const amplitude = Math.max(1, height / 2 - 1);
    const tint = resolveColour(options.tint, '#ec5cff');

    context.beginPath();
    for (let index = 0; index < outputs; index++) {
      const x = (index / outputs) * width;
      context.moveTo(x, midY - Math.max(0.5, clamp(window.maxima[index] ?? 0, 0, 1) * amplitude));
      context.lineTo(x, midY + Math.max(0.5, clamp(-(window.minima[index] ?? 0), 0, 1) * amplitude));
    }
    context.strokeStyle = tint;
    context.lineWidth = 1;
    context.stroke();
  }

  onCleanup(el, observeSize(surface.canvas, () => draw()));
  onCleanup(el, () => surface.dispose());

  surface.measure();
  draw();

  return {
    el,
    setPeaks(next: WaveformPeaks): void {
      peaks = next;
      cache.invalidate();
      draw();
    },
    destroy(): void {
      surface.dispose();
      el.remove();
    },
  };
}
